import React, { useEffect, useRef, useState, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dependent } from "domain/entities/dependent";
import {
  useDependentsByFilter,
  useCreateDependentMutation,
  useUpdateDependentMutation,
  useDeleteDependentMutation,
} from "presentation/hooks/use-query-dependent";
import { translateText, translateRegexMessage } from "infra/translation/translate";
import InputTextComponent from "presentation/components/Shared/InputTextComponent";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import InputNumberComponent from "presentation/components/Shared/InputNumberComponent";
import { MandatorySignal } from "presentation/components/Shared/MandatorySignalComponent";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { EMAIL } from "domain/enum/email";
import Spinner from "presentation/components/Shared/Spinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

interface DependentsModalProps {
  visible: boolean;
  onHide: () => void;
  employeeId: string;
  canEdit: boolean;
}

const DependentsModal: React.FC<DependentsModalProps> = ({
  visible,
  onHide,
  employeeId,
  canEdit,
}) => {
  const toast = useRef<Toast>(null);
  const { email: userEmail } = useAuthStore();
  const [showForm, setShowForm] = useState(false);
  const [editingDependent, setEditingDependent] = useState<Dependent | null>(null);
  const [formData, setFormData] = useState<Dependent>({
    name: "",
    fiscalIdNumber: "",
    birthDate: null,
    childhoodCheckValue: 0,
    educationCheckValue: 0,
    employeeId: employeeId,
  });

  // Calcular idade do dependente para determinar tipo de abono
  const calculateAge = (birthDate: Date | null): number => {
    if (!birthDate) return 0;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const dependentAge = calculateAge(formData.birthDate);
  const isChildhoodCheck = dependentAge < 7;
  const checkLabel = isChildhoodCheck 
    ? `${translateText("Childhood Check Value", "pt")} (<7 anos)`
    : `${translateText("Education Check Value", "pt")} (≥7 anos)`;

  const { data: dependentsData, refetch, isLoading } = useDependentsByFilter({
    employeeId: employeeId,
  });

  const {
    mutate: createDependent,
    isPending: isCreating,
    isSuccess: isCreateSuccess,
    isError: isCreateError,
    error: createError,
  } = useCreateDependentMutation();

  const {
    mutate: updateDependent,
    isPending: isUpdating,
    isSuccess: isUpdateSuccess,
    isError: isUpdateError,
    error: updateError,
  } = useUpdateDependentMutation();

  const {
    mutate: deleteDependent,
    isPending: isDeleting,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
    error: deleteError,
  } = useDeleteDependentMutation();

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      fiscalIdNumber: "",
      birthDate: null,
      childhoodCheckValue: 0,
      educationCheckValue: 0,
      employeeId: employeeId,
    });
    setEditingDependent(null);
    setShowForm(false);
  }, [employeeId]);

  useEffect(() => {
    if (visible) {
      refetch();
    }
  }, [visible, refetch]);

  useEffect(() => {
    if (isCreateSuccess || isUpdateSuccess) {
      toast.current?.show({
        severity: "success",
        summary: translateText("Success", "pt"),
        detail: translateRegexMessage(
          isCreateSuccess
            ? "Dependent created successfully"
            : "Dependent updated successfully",
          "pt"
        ),
        life: 3000,
      });
      resetForm();
      refetch();
    }
  }, [isCreateSuccess, isUpdateSuccess, refetch, resetForm]);

  useEffect(() => {
    if (isDeleteSuccess) {
      toast.current?.show({
        severity: "success",
        summary: translateText("Success", "pt"),
        detail: translateRegexMessage("Dependent deleted successfully", "pt"),
        life: 3000,
      });
      refetch();
    }
  }, [isDeleteSuccess, refetch]);

  useEffect(() => {
    if (isCreateError || isUpdateError || isDeleteError) {
      const error = isCreateError ? createError : isUpdateError ? updateError : deleteError;
      const errorMessage = error?.response?.data?.metadata?.errors?.[0] || "An error occurred";
      
      toast.current?.show({
        severity: "error",
        summary: translateText("Error", "pt"),
        detail: translateRegexMessage(errorMessage, "pt"),
        life: 3000,
      });
    }
  }, [isCreateError, isUpdateError, isDeleteError, createError, updateError, deleteError]);

  const handleSave = () => {
    // Garantir que apenas o campo correto tem valor baseado na idade
    const age = calculateAge(formData.birthDate);
    const dependentToSave = {
      ...formData,
      childhoodCheckValue: age < 7 ? (formData.childhoodCheckValue || 0) : 0,
      educationCheckValue: age >= 7 ? (formData.educationCheckValue || 0) : 0,
      lastModifiedBy: userEmail ?? EMAIL,
    };

    if (editingDependent?.id) {
      updateDependent({ ...dependentToSave, id: editingDependent.id });
    } else {
      createDependent(dependentToSave);
    }
  };

  const handleEdit = (dependent: Dependent) => {
    setEditingDependent(dependent);
    setFormData(dependent);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    confirmDialog({
      message: translateRegexMessage("Are you sure you want to delete this dependent?", "pt"),
      header: translateText("Confirmation", "pt"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: translateText("Yes", "pt"),
      rejectLabel: translateText("No", "pt"),
      accept: () => deleteDependent(id),
    });
  };

  const actionsBodyTemplate = (rowData: Dependent) => {
    if (!canEdit) return null;
    
    return (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <Button
          icon="pi pi-pencil"
          rounded
          text
         
          onClick={() => handleEdit(rowData)}
          tooltip={translateText("Edit", "pt")}
        />
        <Button
          icon="pi pi-times"
          rounded
          text
         
          onClick={() => handleDelete(rowData.id!)}
          tooltip={translateText("Remove", "pt")}
        />
      </div>
    );
  };

  const dateBodyTemplate = (rowData: Dependent) => {
    if (!rowData.birthDate) return "-";
    return new Date(rowData.birthDate).toLocaleDateString("pt-BR");
  };

  const ageBodyTemplate = (rowData: Dependent) => {
    if (!rowData.birthDate) return "-";
    
    const today = new Date();
    const birthDate = new Date(rowData.birthDate);
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    
    if (months < 0 || (months === 0 && today.getDate() < birthDate.getDate())) {
      years--;
      months += 12;
    }
    
    if (today.getDate() < birthDate.getDate()) {
      months--;
    }
    
    if (years < 1) {
      return `${months} ${months === 1 ? "mês" : "meses"}`;
    }
    
    return `${years} ${years === 1 ? "ano" : "anos"}`;
  };

  const dialogFooter = showForm ? (
    <div>
      <Button
        label={translateText("Cancel", "pt")}
        icon="pi pi-times"
        onClick={resetForm}
        className="p-button-text"
      />
      <Button
        label={translateText("Save", "pt")}
        icon="pi pi-check"
        onClick={handleSave}
        disabled={
          !formData.name ||
          !formData.fiscalIdNumber ||
          isCreating ||
          isUpdating
        }
      />
    </div>
  ) : null;

  return (
    <>
      <Dialog
        header={translateText("Dependents", "pt")}
        visible={visible}
        style={{ width: "70vw" }}
        onHide={onHide}
        footer={dialogFooter}
      >
        {isLoading || isDeleting ? (
          <Spinner />
        ) : (
          <>
            {!showForm ? (
              <>
                {canEdit && (
                  <div style={{ marginBottom: "1rem" }}>
                    <Button
                      label={translateText("Add Dependent", "pt")}
                      icon="pi pi-plus"
                      onClick={() => setShowForm(true)}
                    />
                  </div>
                )}
                <DataTable
                  value={dependentsData?.data || []}
                  emptyMessage={translateRegexMessage("No dependents found", "pt")}
                  paginator
                  rows={5}
                  rowsPerPageOptions={[5, 10, 25]}
                >
                  <Column
                    field="name"
                    header={translateText("Name", "pt")}
                    sortable
                  />
                  <Column
                    field="fiscalIdNumber"
                    header={translateText("Fiscal ID", "pt")}
                    sortable
                  />
                  <Column
                    field="birthDate"
                    header={translateText("Birth Date", "pt")}
                    body={dateBodyTemplate}
                    sortable
                  />
                  <Column
                    header="Idade"
                    body={ageBodyTemplate}
                    sortable
                  />
                  <Column
                    field="childhoodCheckValue"
                    header={translateText("Childhood Check", "pt")}
                    sortable
                  />
                  <Column
                    field="educationCheckValue"
                    header={translateText("Education Check", "pt")}
                    sortable
                  />
                  {canEdit && (
                    <Column
                      header={translateText("Actions", "pt")}
                      body={actionsBodyTemplate}
                      style={{ width: "10rem" }}
                    />
                  )}
                </DataTable>
              </>
            ) : (
              <div className="default-4-cols" style={{ gap: "1.5rem", rowGap: "2rem" }}>
                <div className="default-1-col" style={{ marginBottom: "1rem" }}>
                  <InputTextComponent
                    label={`${translateText("Name", "pt")} ${MandatorySignal()}`}
                    fieldName="name"
                    currentObject={formData}
                    getValue={formData.name}
                    setValue={setFormData}
                    isDisable={isCreating || isUpdating}
                  />
                </div>
                <div className="default-1-col" style={{ marginBottom: "1rem" }}>
                  <InputTextComponent
                    label={`${translateText("Fiscal ID", "pt")} ${MandatorySignal()}`}
                    fieldName="fiscalIdNumber"
                    currentObject={formData}
                    getValue={formData.fiscalIdNumber}
                    setValue={setFormData}
                    isDisable={isCreating || isUpdating}
                  />
                </div>
                <div className="default-1-col" style={{ marginBottom: "1rem" }}>
                  <InputDateComponent
                    label={translateText("Birth Date", "pt")}
                    fieldName="birthDate"
                    currentObject={formData}
                    getValue={formData.birthDate}
                    setValue={setFormData}
                    isDisable={isCreating || isUpdating}
                  />
                </div>
                <div className="default-1-col" style={{ marginBottom: "1rem" }}>
                  <InputNumberComponent
                    label={checkLabel}
                    fieldName={isChildhoodCheck ? "childhoodCheckValue" : "educationCheckValue"}
                    currentObject={formData}
                    getValue={isChildhoodCheck ? formData.childhoodCheckValue : formData.educationCheckValue}
                    setValue={setFormData}
                    isDisable={isCreating || isUpdating || !formData.birthDate}
                  />
                  {formData.birthDate && (
                    <small style={{ color: "#6c757d", display: "block", marginTop: "0.25rem" }}>
                      {isChildhoodCheck 
                        ? "Dependente com menos de 7 anos - Abono de Infância"
                        : "Dependente com 7 anos ou mais - Cheque Educação"}
                    </small>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        <Toast ref={toast} position="bottom-right" />
      </Dialog>
      <ConfirmDialog />
    </>
  );
};

export default DependentsModal;
